import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { useMounted } from '@/hooks/mounted.hook';
import ThemeToggle from '@/components/Helper/ThemeToggle';
import AuthCardImg from '@/assets/images/card-auth.png';
import AuthCardImgLight from '@/assets/images/card-auth-light.png';
import Logo from '@/assets/images/LOGO.png';
import LogoLight from '@/assets/images/logo-light.png';
import OverlayTopLeft from '@/assets/images/bg-overlay-top-left.png';
import OverlayTopRight from '@/assets/images/bg-overlay-right.png';
import OverlayBottomLeft from '@/assets/images/bg-overlay-bottom-left.png';
import OverlayTopLeftDark from '@/assets/images/bg-dark-overlay-top-left.png';
import OverlayTopRightDark from '@/assets/images/bg-dark-overlay-right.png';
import OverlayBottomLeftDark from '@/assets/images/bg-dark-overlay-bottom-left.png';

export default function AuthLayout() {
  const mounted = useMounted();
  const { resolvedTheme } = useTheme();
  const { pathname } = useLocation();

  if (!mounted) {
    return null;
  }

  const isDark = resolvedTheme === 'dark';
  const isRegisterPage = pathname === '/register';
  const isInvitationPage = pathname.startsWith('/invite/id/');

  return (
    <main className="relative flex h-[100dvh] overflow-hidden bg-white p-0 dark:bg-[#101010] lg:p-[clamp(1.2rem,1.05vw,2rem)]">
      <section className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
        <div className="relative hidden overflow-hidden rounded-[2.4rem] bg-[#F8F8F8] dark:bg-[#1A1A1A] lg:block">
          <div className="pointer-events-none absolute left-0 top-0 aspect-[433/413] w-[36%] max-w-[43.3rem]">
            <img
              src={isDark ? OverlayTopLeftDark : OverlayTopLeft}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-fill"
            />
          </div>
          <div className="pointer-events-none absolute right-0 top-0 aspect-[592/667] w-[50%] max-w-[59.2rem]">
            <img
              src={isDark ? OverlayTopRightDark : OverlayTopRight}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-fill"
            />
          </div>
          <div className="pointer-events-none absolute bottom-0 left-0 aspect-[500/590] w-[40%] max-w-[50rem]">
            <img
              src={isDark ? OverlayBottomLeftDark : OverlayBottomLeft}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-fill"
            />
          </div>

          <Link
            to="/"
            aria-label="Go to home"
            className="absolute left-[clamp(2.4rem,2.08vw,4rem)] top-[clamp(2.4rem,2.08vw,4rem)] z-10"
          >
            <img
              src={isDark ? Logo : LogoLight}
              alt="nezenx"
              className="h-auto w-[clamp(11rem,7.81vw,15rem)]"
            />
          </Link>

          <div className="relative z-10 flex h-full flex-col items-center px-[clamp(2.4rem,2.08vw,4rem)] pb-[clamp(1.8rem,1.56vw,3rem)] pt-[clamp(6.5rem,4.17vw,8rem)]">
            <div className="mt-[clamp(0.4rem,0.52vw,1rem)] text-center">
              <h1 className="font-montserrat text-[4.8rem] font-bold leading-[1.2] text-custom">
                Time is money
                <br />
                Save it now
              </h1>
              <p className="mx-auto mt-[clamp(1rem,0.83vw,1.6rem)] max-w-[58rem] text-[clamp(1.3rem,0.83vw,1.6rem)] leading-[1.5] text-[#626262] dark:text-[#BFBFBF]">
                Store and manage multiple currencies (USD, <br /> GBP, SGD, AUD, JPY, etc.) in a
                single account.
              </p>
            </div>

            <div className="flex flex-1 items-center justify-center pt-[clamp(1rem,1.04vw,2rem)]">
              <div className="aspect-[471/440] w-[clamp(27rem,min(24.53vw,46.4dvh),47.1rem)]">
                <img
                  src={isDark ? AuthCardImg : AuthCardImgLight}
                  alt="NezenX cards"
                  className="h-full w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="relative min-h-0 overflow-y-auto bg-white px-[2.4rem] dark:bg-[#101010] lg:px-[clamp(2.4rem,4vw,7.5rem)]">
          <div className="mx-auto flex min-h-full w-full max-w-[50rem] flex-col">
            <header className="sticky top-0 z-20 flex h-[8rem] shrink-0 items-center justify-between bg-white pt-[env(safe-area-inset-top)] dark:bg-[#101010] lg:hidden">
              <Link to="/" aria-label="Go to home">
                <img
                  src={isDark ? Logo : LogoLight}
                  alt="nezenx"
                  className="h-auto w-[11.2rem]"
                />
              </Link>
              <ThemeToggle />
            </header>

            <div className="hidden lg:absolute lg:right-[clamp(1.2rem,1.05vw,2rem)] lg:top-[clamp(1.2rem,1.05vw,2rem)] lg:block">
              <ThemeToggle />
            </div>

            <div
              className={`w-full flex-1 pb-[max(2.4rem,env(safe-area-inset-bottom))] pt-[3.2rem] sm:pt-[4rem] lg:pb-[2rem] ${
                isInvitationPage
                  ? 'flex items-center pt-0 sm:pt-0 lg:pt-[2rem]'
                  : isRegisterPage
                  ? 'lg:pt-[clamp(6rem,10vh,10rem)]'
                  : 'lg:flex lg:items-center lg:pt-[2rem]'
              }`}
            >
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
